import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useData } from '@hooks/useData';
import { useTranslation } from 'react-i18next';
import {
    buildCookieConsentConfig,
    buildPolicyUrl,
    COOKIE_CONSENT_STORAGE_NAME,
    COOKIE_CONSENT_ELEMENT_ID,
    KlaroConfig,
} from '@services/cookieConsent';

declare global {
    interface Window {
        klaro?: {
            setup: (config: KlaroConfig) => void;
            show: (config?: KlaroConfig) => void;
            getManager: (config?: KlaroConfig) => {
                confirmed: boolean;
                watch: (watcher: KlaroWatcher) => void;
                unwatch: (watcher: KlaroWatcher) => void;
            };
        };
    }
}

type KlaroWatcher = {
    update: (manager: { confirmed: boolean }, eventName: string) => void;
};

const DEFAULT_LANGUAGES = ['pt', 'en', 'fr', 'es', 'de'];

const hasStoredDecision = () => {
    try {
        return Boolean(localStorage.getItem(COOKIE_CONSENT_STORAGE_NAME));
    } catch {
        return false;
    }
};

export const useCookieConsent = () => {
    const { allLanguages, allSettings } = useData();
    const { t, i18n } = useTranslation();
    const [hasDecision, setHasDecision] = useState<boolean>(() => hasStoredDecision());
    const [isReady, setIsReady] = useState<boolean>(false);
    const configRef = useRef<KlaroConfig | null>(null);
    const watcherRef = useRef<KlaroWatcher | null>(null);
    const initialPromptShownRef = useRef<boolean>(false);

    const fallbackLanguage = useMemo(() => {
        const apiDefault = allLanguages?.data?.find((language) => language.default === 1)?.language_code;
        return apiDefault || 'pt';
    }, [allLanguages]);

    const hasAnalyticsConfig = useMemo(() => {
        const analyticsSetting = (allSettings?.data as Record<string, unknown> | undefined)?.['google_analytics'];
        if (typeof analyticsSetting === 'string') {
            return analyticsSetting.trim().length > 0;
        }
        return Boolean(analyticsSetting);
    }, [allSettings]);

    const languages = useMemo(() => {
        const apiLanguages =
            allLanguages?.data?.map((language) => language.language_code).filter((code): code is string => Boolean(code)) || [];
        return Array.from(new Set([...DEFAULT_LANGUAGES, ...apiLanguages]));
    }, [allLanguages]);

    const analyticsMeasurementId = useMemo(() => {
        const setting = (allSettings?.data as Record<string, unknown> | undefined)?.['google_analytics'];
        if (typeof setting === 'string') {
            const trimmed = setting.trim();
            return trimmed.length > 0 ? trimmed : null;
        }
        if (setting && typeof setting === 'object') {
            const maybeId = (setting as Record<string, unknown>)['id'] || (setting as Record<string, unknown>)['measurementId'];
            if (typeof maybeId === 'string' && maybeId.trim().length > 0) return maybeId.trim();
        }
        return null;
    }, [allSettings]);

    const resolvePolicyUrl = useCallback((language?: string) => buildPolicyUrl(language, fallbackLanguage), [fallbackLanguage]);

    const cookieConfig = useMemo(() => {
        if (!hasAnalyticsConfig) {
            return null;
        }
        return buildCookieConsentConfig({
            languages,
            fallbackLanguage,
            currentLanguage: i18n.language,
            getTranslator: i18n.getFixedT.bind(i18n),
            onPreferenceChange: (service, consent) => {
                window.dispatchEvent(new CustomEvent('cookie-preferences-change', { detail: { service, consent } }));
            },
            policyUrlResolver: resolvePolicyUrl,
        });
    }, [languages, fallbackLanguage, i18n, resolvePolicyUrl, hasAnalyticsConfig]);

    const detachWatcher = useCallback(() => {
        if (!hasAnalyticsConfig || !window.klaro || !watcherRef.current || !configRef.current) {
            return;
        }
        const manager = window.klaro.getManager(configRef.current);
        manager?.unwatch(watcherRef.current);
        watcherRef.current = null;
    }, [hasAnalyticsConfig]);

    const attachWatcher = useCallback(() => {
        if (!hasAnalyticsConfig || !window.klaro || !configRef.current) {
            return;
        }
        const manager = window.klaro.getManager(configRef.current);
        if (!manager) {
            return;
        }
        detachWatcher();
        const watcher: KlaroWatcher = {
            update: (_, eventName) => {
                if (eventName === 'saveConsents') {
                    setHasDecision(true);
                }
            },
        };
        watcherRef.current = watcher;
        manager.watch(watcher);
        setHasDecision(manager.confirmed || hasStoredDecision());
    }, [detachWatcher, hasAnalyticsConfig]);

    useEffect(() => {
        configRef.current = cookieConfig;
    }, [cookieConfig]);

    useEffect(() => {
        let cancelled = false;

        const loadKlaro = async () => {
            if (typeof window === 'undefined' || !cookieConfig || !hasAnalyticsConfig) return;

            if (!window.klaro) {
                const klaroModule = await import('klaro/dist/klaro-no-translations.js');
                const klaroInstance = (klaroModule && (klaroModule.default || klaroModule)) as Window['klaro'];
                if (klaroInstance) {
                    window.klaro = klaroInstance;
                }
            }
            if (cancelled || !window.klaro || !configRef.current) {
                return;
            }

            const alreadyDecided = hasStoredDecision();
            window.klaro.setup(configRef.current);
            attachWatcher();
            setIsReady(true);
            setHasDecision((prev) => prev || alreadyDecided);

            if (!alreadyDecided && !initialPromptShownRef.current) {
                initialPromptShownRef.current = true;
                window.klaro.show(configRef.current);
            }
        };

        if (hasAnalyticsConfig) {
            loadKlaro();
        }
        return () => {
            cancelled = true;
            detachWatcher();
        };
    }, [cookieConfig, attachWatcher, detachWatcher, hasAnalyticsConfig]);

    // GA4 injection controlled by Klaro consent (service: "analytics")
    const gaInitializedRef = useRef<boolean>(false);
    const currentGaIdRef = useRef<string | null>(null);

    const setGADisabledFlag = useCallback((id: string, disabled: boolean) => {
        try {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (window as any)[`ga-disable-${id}`] = disabled;
        } catch {
        }
    }, []);

    const injectGA = useCallback(
        (id: string) => {
            if (typeof window === 'undefined' || !id) return;

            if (gaInitializedRef.current && currentGaIdRef.current === id) {
                return;
            }
            
            if (gaInitializedRef.current && currentGaIdRef.current && currentGaIdRef.current !== id) {
                setGADisabledFlag(currentGaIdRef.current, true);
            }
            
            setGADisabledFlag(id, false);

            // Load gtag.js
            const existingSrc = document.getElementById('ga4-gtag-src');
            if (!existingSrc) {
                const script = document.createElement('script');
                script.id = 'ga4-gtag-src';
                script.async = true;
                script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
                document.head.appendChild(script);
            }

            const existingInit = document.getElementById('ga4-gtag-init');
            if (!existingInit) {
                const inline = document.createElement('script');
                inline.id = 'ga4-gtag-init';
                inline.innerHTML = `window.dataLayer = window.dataLayer || []; function gtag(){dataLayer.push(arguments);} gtag('js', new Date());`;
                document.head.appendChild(inline);
            }

            const configTag = document.createElement('script');
            configTag.setAttribute('data-ga-id', id);
            configTag.innerHTML = `window.dataLayer = window.dataLayer || []; function gtag(){dataLayer.push(arguments);} gtag('config', '${id}', { anonymize_ip: true });`;
            document.head.appendChild(configTag);

            gaInitializedRef.current = true;
            currentGaIdRef.current = id;
        },
        [setGADisabledFlag]
    );

    const removeGA = useCallback((id?: string) => {
        try {
            const configScripts = Array.from(document.querySelectorAll('script[data-ga-id]')) as HTMLScriptElement[];
            configScripts.forEach((el) => {
                if (!id || el.getAttribute('data-ga-id') === id) {
                    el.parentNode?.removeChild(el);
                }
            });

            const srcEl = document.getElementById('ga4-gtag-src');
            if (srcEl) srcEl.parentNode?.removeChild(srcEl);
            const initEl = document.getElementById('ga4-gtag-init');
            if (initEl) initEl.parentNode?.removeChild(initEl);

            try { delete (window as any).gtag; } catch {}
            try { (window as any).dataLayer = []; } catch {}

            gaInitializedRef.current = false;
            currentGaIdRef.current = null;
        } catch {
        }
    }, []);

    useEffect(() => {
        if (!hasAnalyticsConfig || !analyticsMeasurementId) return;

        const handleConsentEvent = (e: Event) => {
            const evt = e as CustomEvent<{ service: string; consent: boolean }>;
            if (!evt.detail) return;
            const { service, consent } = evt.detail;
            if (service !== 'analytics') return;
            if (consent) {
                injectGA(analyticsMeasurementId);
            } else {
                setGADisabledFlag(analyticsMeasurementId, true);
                removeGA(analyticsMeasurementId);
            }
        };

        window.addEventListener('cookie-preferences-change', handleConsentEvent as EventListener);

        try {
            if (window.klaro && configRef.current) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const manager: any = window.klaro.getManager(configRef.current);
                let consent = false;
                if (manager) {
                    if (typeof manager.getConsent === 'function') {
                        consent = !!manager.getConsent('analytics-suite');
                    } else if (manager.consents) {
                        consent = !!manager.consents['analytics-suite'];
                    }
                }
                if (consent) {
                    injectGA(analyticsMeasurementId);
                } else {
                    setGADisabledFlag(analyticsMeasurementId, true);
                    removeGA(analyticsMeasurementId);
                }
            }
        } catch {
            // ignore
        }

        return () => {
            window.removeEventListener('cookie-preferences-change', handleConsentEvent as EventListener);
        };
    }, [hasAnalyticsConfig, analyticsMeasurementId, injectGA, setGADisabledFlag, removeGA]);

    // Ensure privacy policy links open in the same tab (avoid blank page/new tab)
    useEffect(() => {
        if (!hasAnalyticsConfig) return;

        const policyUrls = new Set<string>();
        try {
            const all = Array.from(new Set([fallbackLanguage, ...languages]));
            all.forEach((lang) => {
                // Use undefined for default language resolution to mirror Klaro's default mapping
                const isDefault = lang === fallbackLanguage;
                const rel = resolvePolicyUrl(isDefault ? undefined : lang);
                const abs = new URL(rel, window.location.origin).href;
                policyUrls.add(abs);
            });
        } catch {
            // ignore URL resolution issues
        }

        const onClick = (e: MouseEvent) => {
            const target = e.target as Element | null;
            if (!target) return;
            const anchor = (target.closest ? target.closest('a[href]') : null) as HTMLAnchorElement | null;
            if (!anchor || !anchor.href) return;

            // Limit to clicks inside Klaro element, if present
            const container = anchor.closest(`#${COOKIE_CONSENT_ELEMENT_ID}`);
            if (!container) return;

            if (policyUrls.has(anchor.href)) {
                e.preventDefault();
                // Force same-tab navigation
                window.location.assign(anchor.href);
            }
        };

        document.addEventListener('click', onClick, true);
        return () => document.removeEventListener('click', onClick, true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasAnalyticsConfig, languages, fallbackLanguage, resolvePolicyUrl]);

    const openManager = useCallback(() => {
        if (!hasAnalyticsConfig || !window.klaro || !configRef.current) return;
        window.klaro.show(configRef.current);
    }, [hasAnalyticsConfig]);

    return {
        manageButtonLabel: t('cookie-manager.manage-button'),
        openManager,
        shouldDisplayTrigger: hasAnalyticsConfig && isReady && hasDecision,
        shouldRender: hasAnalyticsConfig,
    };
};
